import type { ItemKind, Prompt, WorkflowStep } from '../types';

export const ITEM_KINDS: ItemKind[] = ['prompt', 'skill', 'workflow', 'gem'];

export const KIND_META: Record<ItemKind, {
  label: string;
  plural: string;
  newLabel: string;
  defaultName: string;
  /** Tailwind-Klassen, abgeleitet aus den Farb-Tokens in index.css */
  text: string;
  bg: string;
  border: string;
  hint: string;
}> = {
  prompt: {
    label: 'Prompt',
    plural: 'Prompts',
    newLabel: 'Neuer Prompt',
    defaultName: 'Neuer Prompt',
    text: 'text-kind-prompt',
    bg: 'bg-kind-prompt',
    border: 'border-kind-prompt',
    hint: 'Einzelne Anweisung zum Kopieren und Einfügen',
  },
  skill: {
    label: 'Skill',
    plural: 'Skills',
    newLabel: 'Neuer Skill',
    defaultName: 'Neuer Skill',
    text: 'text-kind-skill',
    bg: 'bg-kind-skill',
    border: 'border-kind-skill',
    hint: 'SKILL.md mit Beschreibung, Anweisungen und Zusatzdateien',
  },
  workflow: {
    label: 'Workflow',
    plural: 'Workflows',
    newLabel: 'Neuer Workflow',
    defaultName: 'Neuer Workflow',
    text: 'text-kind-workflow',
    bg: 'bg-kind-workflow',
    border: 'border-kind-workflow',
    hint: 'Feste Abfolge von Schritten, die Prompts und Skills einbinden kann',
  },
  gem: {
    label: 'Gem',
    plural: 'Gems',
    newLabel: 'Neues Gem',
    defaultName: 'Neues Gem',
    text: 'text-kind-gem',
    bg: 'bg-kind-gem',
    border: 'border-kind-gem',
    hint: 'Systemprompt für ein Gemini-Gem, mit Wissensdateien',
  },
};

/** Dateiname des Systemprompts beim Herunterladen eines Gems */
export const GEM_MAIN_FILE = 'SYSTEMPROMPT.md';

export const getKind = (item: Pick<Prompt, 'kind'> | null | undefined): ItemKind => item?.kind ?? 'prompt';

/** Anzahlwort mit korrekter Einzahl/Mehrzahl, z. B. "1 Skill", "3 Workflows" */
export const countLabel = (count: number, kind: ItemKind | 'all') => {
  if (kind === 'all') return `${count} ${count === 1 ? 'Eintrag' : 'Einträge'}`;
  return `${count} ${count === 1 ? KIND_META[kind].label : KIND_META[kind].plural}`;
};

/** Skill-Namen nach Claude-Konvention: Kleinbuchstaben, Ziffern, Bindestriche, max. 64 Zeichen */
export const slugify = (value: string) =>
  value
    .toLowerCase()
    .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 64)
    .replace(/-+$/, '');

export const SKILL_SLUG_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/;
export const SKILL_DESCRIPTION_MAX = 1024;

export const getSkillSlug = (item: Prompt) => item.slug || slugify(item.name) || 'neuer-skill';

const yamlString = (value: string) => {
  const singleLine = value.replace(/\s*\n\s*/g, ' ').trim();
  // Anführungszeichen nur, wenn YAML sie wirklich braucht
  if (/^[\w\s.,;()äöüÄÖÜß/–-]*$/.test(singleLine) && !/^[-\s]/.test(singleLine)) return singleLine;
  return `"${singleLine.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;
};

export const buildSkillMarkdown = (item: Prompt) => {
  const lines = [
    '---',
    `name: ${getSkillSlug(item)}`,
    `description: ${yamlString(item.description || '')}`,
    '---',
    '',
    item.content || '',
  ];
  return lines.join('\n').trimEnd() + '\n';
};

const unquote = (value: string) => {
  const v = value.trim();
  if ((v.startsWith('"') && v.endsWith('"')) || (v.startsWith("'") && v.endsWith("'"))) {
    return v.slice(1, -1).replace(/\\"/g, '"').replace(/\\\\/g, '\\');
  }
  return v;
};

/** Liest eine SKILL.md. Unterstützt einzeilige Werte und Block-Scalars (| und >). */
export const parseSkillMarkdown = (raw: string): { slug: string; description: string; body: string } => {
  const text = raw.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n');
  const match = text.match(/^---\n([\s\S]*?)\n---\n?([\s\S]*)$/);
  if (!match) return { slug: '', description: '', body: text.trim() };

  const meta: Record<string, string> = {};
  const metaLines = match[1].split('\n');
  for (let i = 0; i < metaLines.length; i++) {
    const m = metaLines[i].match(/^([A-Za-z_-]+):\s*(.*)$/);
    if (!m) continue;
    const [, key, rest] = m;
    if (rest === '|' || rest === '>' || rest === '|-' || rest === '>-') {
      const block: string[] = [];
      while (i + 1 < metaLines.length) {
        const nextLine = metaLines[i + 1];
        if (/^\s+\S/.test(nextLine)) {
          block.push(metaLines[++i].trim());
        } else if (/^\s*$/.test(nextLine)) {
          let hasIndentedFollowup = false;
          for (let j = i + 2; j < metaLines.length; j++) {
            if (/^\s+\S/.test(metaLines[j])) {
              hasIndentedFollowup = true;
              break;
            }
            if (/^[A-Za-z_-]+:\s*/.test(metaLines[j])) {
              break;
            }
          }
          if (hasIndentedFollowup) {
            i++;
            block.push('');
          } else {
            break;
          }
        } else {
          break;
        }
      }
      meta[key] = rest.startsWith('|') ? block.join('\n') : block.join(' ');
    } else {
      meta[key] = unquote(rest);
    }
  }
  return { slug: meta.name || '', description: meta.description || '', body: match[2].trim() };
};

/** Workflow als Markdown; verknüpfte Einträge werden auf Wunsch mit vollem Inhalt eingebettet. */
export const buildWorkflowMarkdown = (
  item: Prompt,
  resolve: (id: string) => Prompt | undefined,
  { embedReferences = true }: { embedReferences?: boolean } = {},
) => {
  const out: string[] = [`# ${item.name}`];
  if (item.description?.trim()) out.push('', item.description.trim());
  (item.steps || []).forEach((step: WorkflowStep, i: number) => {
    out.push('', `## Schritt ${i + 1}: ${step.title || 'Ohne Titel'}`);
    const stepContent = (step.content || '').trim();
    if (stepContent) out.push('', stepContent);
    const ref = step.refId ? resolve(step.refId) : undefined;
    if (ref) {
      const kind = getKind(ref);
      if (kind === 'skill') {
        out.push('', `Skill verwenden: \`${getSkillSlug(ref)}\``);
      } else if (embedReferences) {
        out.push('', `Prompt „${ref.name}“:`, '', '```', (ref.content || '').trim(), '```');
      } else {
        out.push('', `Prompt verwenden: „${ref.name}“`);
      }
    }
  });
  return out.join('\n') + '\n';
};

/** Alle durchsuchbaren Texte eines Eintrags in Kleinbuchstaben */
export const searchableText = (item: Prompt) =>
  [
    item.name,
    item.content,
    item.description,
    item.slug,
    ...(item.tags || []),
    ...(item.files || []).flatMap(f => [f.path, f.content]),
    ...(item.steps || []).flatMap(s => [s.title, s.content]),
  ]
    .filter(Boolean)
    .join('\n')
    .toLowerCase();

/** Kurzer Vorschautext für Listen */
export const excerptFor = (item: Prompt) => {
  const kind = getKind(item);
  if (kind === 'skill' || kind === 'gem') return item.description || item.content;
  if (kind === 'workflow') {
    if (item.description) return item.description;
    return (item.steps || []).map(s => s.title).filter(Boolean).join(' → ');
  }
  return item.content;
};

export const downloadText = (filename: string, text: string, type = 'text/markdown;charset=utf-8') => {
  const blob = new Blob([text], { type });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(link.href), 1000);
};

export const downloadBlob = (filename: string, blob: Blob) => {
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  setTimeout(() => URL.revokeObjectURL(link.href), 1000);
};

const crcTable = new Uint32Array(256);
for (let i = 0; i < 256; i++) {
  let c = i;
  for (let k = 0; k < 8; k++) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
  crcTable[i] = c;
}

export function computeCrc32(bytes: Uint8Array): number {
  let c = 0 ^ (-1);
  for (let i = 0; i < bytes.length; i++) c = (c >>> 8) ^ crcTable[(c ^ bytes[i]) & 0xFF];
  return (c ^ (-1)) >>> 0;
}

/**
 * Erzeugt ein unkomprimiertes ZIP-Archiv (Store-Format) ohne externe Abhängigkeiten.
 * Behält Unterverzeichnisse (z. B. scripts/, references/) exakt bei.
 */
export function buildZipBlob(files: { path: string; content: string | Uint8Array }[]): Blob {
  const textEncoder = new TextEncoder();
  const fileRecords = files.map(f => {
    const nameBytes = textEncoder.encode(f.path.replace(/^\/+/, ''));
    const contentBytes = typeof f.content === 'string' ? textEncoder.encode(f.content) : f.content;
    const crc = computeCrc32(contentBytes);
    return { nameBytes, contentBytes, crc };
  });

  let totalSize = 0;
  for (const r of fileRecords) {
    totalSize += 30 + r.nameBytes.length + r.contentBytes.length;
    totalSize += 46 + r.nameBytes.length;
  }
  totalSize += 22;

  const buf = new Uint8Array(totalSize);
  const view = new DataView(buf.buffer);
  let offset = 0;
  const centralDirOffsets: number[] = [];

  for (const r of fileRecords) {
    centralDirOffsets.push(offset);
    view.setUint32(offset, 0x04034b50, true);
    view.setUint16(offset + 4, 20, true);
    view.setUint16(offset + 6, 0x0800, true);
    view.setUint16(offset + 8, 0, true);
    view.setUint16(offset + 10, 0, true);
    view.setUint16(offset + 12, 0, true);
    view.setUint32(offset + 14, r.crc, true);
    view.setUint32(offset + 18, r.contentBytes.length, true);
    view.setUint32(offset + 22, r.contentBytes.length, true);
    view.setUint16(offset + 26, r.nameBytes.length, true);
    view.setUint16(offset + 28, 0, true);
    buf.set(r.nameBytes, offset + 30);
    buf.set(r.contentBytes, offset + 30 + r.nameBytes.length);
    offset += 30 + r.nameBytes.length + r.contentBytes.length;
  }

  const centralDirStart = offset;
  for (let i = 0; i < fileRecords.length; i++) {
    const r = fileRecords[i];
    const localOffset = centralDirOffsets[i];
    view.setUint32(offset, 0x02014b50, true);
    view.setUint16(offset + 4, 20, true);
    view.setUint16(offset + 6, 20, true);
    view.setUint16(offset + 8, 0x0800, true);
    view.setUint16(offset + 10, 0, true);
    view.setUint16(offset + 12, 0, true);
    view.setUint16(offset + 14, 0, true);
    view.setUint32(offset + 16, r.crc, true);
    view.setUint32(offset + 20, r.contentBytes.length, true);
    view.setUint32(offset + 24, r.contentBytes.length, true);
    view.setUint16(offset + 28, r.nameBytes.length, true);
    view.setUint16(offset + 30, 0, true);
    view.setUint16(offset + 32, 0, true);
    view.setUint16(offset + 34, 0, true);
    view.setUint16(offset + 36, 0, true);
    view.setUint32(offset + 38, 0, true);
    view.setUint32(offset + 42, localOffset, true);
    buf.set(r.nameBytes, offset + 46);
    offset += 46 + r.nameBytes.length;
  }

  const centralDirSize = offset - centralDirStart;
  view.setUint32(offset, 0x06054b50, true);
  view.setUint16(offset + 4, 0, true);
  view.setUint16(offset + 6, 0, true);
  view.setUint16(offset + 8, fileRecords.length, true);
  view.setUint16(offset + 10, fileRecords.length, true);
  view.setUint32(offset + 12, centralDirSize, true);
  view.setUint32(offset + 16, centralDirStart, true);
  view.setUint16(offset + 20, 0, true);

  return new Blob([buf], { type: 'application/zip' });
}
